/**
 * AuthDialog - Standalone auth page for Office Dialog API
 *
 * Loaded in Office.context.ui.displayDialogAsync() window.
 *
 * Microsoft flow (redirect-based — popup is unreliable inside the Office Mac
 * dialog, where it escapes to the system browser and can't return):
 *  1. User clicks "Continue with Microsoft"
 *  2. signInWithRedirect navigates THIS dialog window to Microsoft and back
 *  3. On return, getRedirectResult (mount effect) yields the user
 *  4. We mint a bridge custom token (same-origin /api/auth/mint-bridge) and
 *     messageParent it to the task pane, which signs in with it
 *
 * Email/password flow stays in-dialog and uses the same bridge handoff.
 *
 * Route: /chat/auth
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Alert, Button, TextField, Divider, Link, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { signInWithRedirect, signInWithPopup, getRedirectResult, onAuthStateChanged, OAuthProvider, signInWithCustomToken } from 'firebase/auth';
import { auth, signInWithEmail, signUpWithEmail, resetPassword } from '../../shared/services/firebase';
import { sendAuthResultToParent } from '../../shared/services/office/OfficeAuth';
import { getAppName } from './appCopy';

/* global Office */

/**
 * True when this dialog is running under Office on the web (as opposed to a
 * desktop Office webview). On the web the Dialog API is a monitored browser
 * popup, so a cross-origin redirect trips DialogEventReceived 12006; we use
 * signInWithPopup there instead. Defaults to false (desktop/redirect) if the
 * platform can't be read, which preserves the desktop behavior.
 */
function isOfficeWeb() {
  // PRIMARY: the task pane forwards its (reliable) platform value as a
  // `hostPlatform` query param when it opens this dialog. Inside an Office
  // dialog window Office.context.platform is often undefined, so the param is
  // the trustworthy signal that we are on Office for the web.
  try {
    const fromParent = new URLSearchParams(window.location.search).get('hostPlatform');
    if (fromParent) return fromParent === 'OfficeOnline';
  } catch (e) { /* fall through to the Office.context probe */ }
  // FALLBACK: best-effort platform probe (works on some hosts/contexts).
  try {
    return typeof Office !== 'undefined'
      && !!Office.context
      && Office.context.platform === Office.PlatformType.OfficeOnline;
  } catch (e) {
    return false;
  }
}

/**
 * Mint a Firebase custom token for the parent (taskpane) window. The dialog
 * already has a valid Firebase session post-signInWithPopup; we use its ID
 * token to ask the backend for a custom token the parent can sign in with.
 *
 * Why custom tokens instead of re-using the OAuth credential: Office's dialog
 * webview may not share IndexedDB with the taskpane (varies by host platform),
 * AND Microsoft id_tokens are single-use against Firebase's verifyAssertion
 * endpoint. signInWithCustomToken sidesteps both — works for all providers.
 *
 * Throws on failure; caller decides how to surface to the user.
 */
async function mintBridgeTokenForParent(user) {
  const idToken = await user.getIdToken();
  // SAME-ORIGIN ONLY: route through the Firebase Hosting rewrite
  // (/api/auth/mint-bridge → mintAuthBridgeToken) instead of a cross-origin
  // POST to cloudfunctions.net. A cross-origin call from inside the Office
  // Mac dialog webview *stalls* — the request reaches the backend but the
  // response never makes it back to the webview, so `bridgeCustomToken` came
  // back null and the parent fell into the (storage-isolated) reload path and
  // never signed in. This is the identical fix already applied to the SSO
  // exchange (/api/auth/office-sso). AbortController guarantees we surface a
  // failure instead of hanging forever.
  const MINT_TIMEOUT_MS = 15000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MINT_TIMEOUT_MS);
  let response;
  try {
    response = await fetch('/api/auth/mint-bridge', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) {
    const errBody = await response.text().catch(() => '');
    throw new Error(`mintAuthBridgeToken failed (${response.status}): ${errBody}`);
  }
  const data = await response.json();
  if (!data.customToken) {
    throw new Error('mintAuthBridgeToken returned no customToken');
  }
  return data.customToken;
}

/**
 * Unify the add-in's Microsoft identity with Office SSO.
 *
 * The dialog's signInWithPopup/Redirect uses Firebase's NATIVE microsoft.com
 * provider, which mints a federated uid that is NOT `microsoft_{oid}`. When the
 * email already belongs to the SSO-created account, Firebase throws
 * auth/account-exists-with-different-credential and the federated sign-in cannot
 * proceed. We recover exactly like the website (firebase.js signInWithMicrosoft):
 * take the Microsoft id_token off the pending credential and exchange it via
 * /api/auth/microsoft-web for a custom token bound to the SAME canonical uid
 * (exchangeWebMicrosoftToken resolves microsoft_{oid} -> reuse-by-email -> create,
 * identical to the SSO exchange), then sign in with that. This is what keeps a
 * pane signed in through the dialog in the SAME Conductor room and chat history
 * as a pane signed in via SSO, instead of fragmenting one human across two uids.
 *
 * Returns the reconciled user on success, or null (caller keeps its error path).
 */
async function reconcileMicrosoftIdentity(error) {
  if (error?.code !== 'auth/account-exists-with-different-credential') return null;
  try {
    const pendingCred = OAuthProvider.credentialFromError(error);
    const msIdToken = pendingCred?.idToken;
    if (!msIdToken) { reportDiag('ms_reconcile_no_token'); return null; }
    const resp = await fetch('/api/auth/microsoft-web', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken: msIdToken }),
    });
    if (!resp.ok) { reportDiag('ms_reconcile_http', resp.status); return null; }
    const data = await resp.json();
    if (!data?.customToken) { reportDiag('ms_reconcile_no_customtoken'); return null; }
    const result = await signInWithCustomToken(auth, data.customToken);
    reportDiag('ms_reconcile_ok', result?.user?.uid);
    return result.user;
  } catch (e) {
    reportDiag('ms_reconcile_err', e?.message);
    return null;
  }
}

// Relay a diagnostic breadcrumb to the parent task pane. The dialog runs in its
// own window with a console we cannot see during Office-web testing, so we mirror
// key steps to the parent (which logs them as "[AuthDialog→parent] ..."). Safe to
// call repeatedly; the parent treats __diag messages as logs, not auth results.
function reportDiag(step, detail) {
  try {
    if (typeof Office !== 'undefined' && Office.context && Office.context.ui && Office.context.ui.messageParent) {
      Office.context.ui.messageParent(JSON.stringify({
        __diag: true,
        step,
        detail: detail == null ? '' : String(detail).slice(0, 300),
      }));
    }
  } catch (e) { /* ignore */ }
}

export default function AuthDialog() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailMode, setEmailMode] = useState('signin'); // signin | signup | reset
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const triggeredRef = useRef(false);
  const completedRef = useRef(false);
  // Host app forwarded by the task pane (?app=excel|word|powerpoint) so the
  // dialog copy reads "...inside Excel/Word" rather than always "PowerPoint".
  const appName = getAppName(new URLSearchParams(window.location.search).get('app'), 'PowerPoint');

  // Single completion path for ALL providers (Microsoft redirect, email/password,
  // and the onAuthStateChanged backup below). Mints the same-origin bridge custom
  // token and hands it to the parent task pane — the only handoff that works
  // across the dialog/task-pane storage boundary on PowerPoint desktop. Guarded
  // so it runs exactly once even when both getRedirectResult and the auth-state
  // listener fire for the same sign-in.
  const completeSignIn = async (user, msCredential = null) => {
    if (completedRef.current || !user) return;
    completedRef.current = true;
    console.log('[AuthDialog] Completing sign-in, minting bridge token for parent');
    reportDiag('complete_start', user?.email);
    setSuccess(true);
    let bridgeCustomToken = null;
    try {
      bridgeCustomToken = await mintBridgeTokenForParent(user);
      reportDiag('mint_ok');
    } catch (mintErr) {
      reportDiag('mint_fail', mintErr.message);
      console.warn('[AuthDialog] Bridge token mint failed:', mintErr.message);
    }
    reportDiag('send_final', `hasBridge=${!!bridgeCustomToken}`);
    sendAuthResultToParent({
      bridgeCustomToken,
      microsoftCredentialJSON: msCredential ? msCredential.toJSON() : null,
      microsoftIdToken: msCredential?.idToken || null,
      microsoftAccessToken: msCredential?.accessToken || null,
      token: await user.getIdToken(),
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
      },
    });
    setTimeout(() => { window.close(); }, 2000);
  };

  // Microsoft / Azure AD sign-in. Restack is a PowerPoint plugin, so MS 365
  // accounts are the dominant identity provider for our enterprise customers
  // — needs to be a first-class option, not buried in "other ways to sign in".
  const handleMicrosoftSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      const provider = new OAuthProvider('microsoft.com');
      // tenant=common accepts work/school AND personal Microsoft accounts;
      // restrict to a specific tenant ID later if a customer requires SSO-only.
      provider.setCustomParameters({ tenant: 'common', prompt: 'select_account' });
      provider.addScope('openid');
      provider.addScope('email');
      provider.addScope('profile');

      // Office on the web: signInWithPopup. The web Dialog API is a monitored
      // browser popup, so a full-page redirect navigates it cross-origin to
      // Microsoft and Office reports it to the task pane as 12006 ("Auth dialog
      // was closed by user") before the user can finish (AppSource cert failure
      // 1120.3.2.7, Edge). A popup keeps THIS window on re-stack.com, so Office
      // never sees a cross-origin nav. completeSignIn handles the bridge handoff.
      const web = isOfficeWeb();
      console.log('[AuthDialog] Microsoft sign-in path:', web ? 'popup (Office web)' : 'redirect (desktop)');
      reportDiag('ms_click', `web=${web} hostPlatform=${new URLSearchParams(window.location.search).get('hostPlatform')}`);
      if (web) {
        reportDiag('ms_popup_start');
        const result = await signInWithPopup(auth, provider);
        reportDiag('ms_popup_done', result?.user?.email);
        await completeSignIn(result.user, OAuthProvider.credentialFromResult(result));
        return;
      }

      // Desktop (Mac/Windows webview): signInWithRedirect navigates THIS dialog
      // window to Microsoft and back to /chat/auth. Completion is handled on
      // return by getRedirectResult / onAuthStateChanged in the mount effect.
      // We must NOT use a popup here: in PowerPoint desktop (Mac WKWebView) a
      // popup is handed off to the system browser, which can never message back
      // into the Office dialog, so sign-in never completes ("goes to the browser,
      // never returns"). Redirect keeps everything inside the dialog window.
      await signInWithRedirect(auth, provider);
      // Execution stops here on desktop — the window navigates away. Do not add
      // redirect-path code below.
    } catch (err) {
      reportDiag('ms_error', `${err.code || ''} ${err.message || ''}`);
      // The email already belongs to the SSO account (microsoft_{oid}); reconcile
      // to that uid so this pane shares the Conductor room + chat history with an
      // SSO-signed pane instead of failing here with account-exists.
      const reconciled = await reconcileMicrosoftIdentity(err);
      if (reconciled) { await completeSignIn(reconciled); return; }
      // Surface enough detail to debug AADSTS / Firebase config errors that
      // would otherwise show up as a cross-origin "Script error" placeholder.
      console.error('[AuthDialog] Microsoft sign-in failed:', {
        code: err.code,
        message: err.message,
        customData: err.customData,
        stack: err.stack
      });
      let userMessage = err.message || 'Microsoft sign-in failed. Please try again.';
      if (err.code === 'auth/operation-not-allowed') {
        userMessage = 'Microsoft sign-in isn\'t enabled in this Firebase project. Contact support.';
      } else if (err.code === 'auth/unauthorized-domain') {
        userMessage = 'This domain isn\'t authorized for Microsoft sign-in. Contact support.';
      } else if (err.code === 'auth/popup-blocked') {
        userMessage = 'Your browser blocked the sign-in popup. Please allow popups and try again.';
      } else if (err.code === 'auth/popup-closed-by-user') {
        userMessage = 'Sign-in was cancelled before completion.';
      } else if (/AADSTS50011/i.test(err.message || '')) {
        userMessage = 'Microsoft sign-in is misconfigured (Azure redirect URI mismatch). Contact support.';
      }
      setError(userMessage);
      setLoading(false);
      setShowEmailForm(true);
    }
  };

  const handleEmailSubmit = async (e) => {
    e?.preventDefault();
    if (emailLoading) return;
    setError(null);
    setEmailLoading(true);
    try {
      if (emailMode === 'reset') {
        await resetPassword(emailInput.trim());
        setError(null);
        setEmailMode('signin');
        setError('Reset link sent! Check your email.');
      } else if (emailMode === 'signup') {
        // Email verification removed — signup signs the user in immediately,
        // so bridge the new session straight to the task pane.
        const user = await signUpWithEmail(emailInput.trim(), passwordInput, nameInput.trim() || null);
        await completeSignIn(user);
      } else {
        const user = await signInWithEmail(emailInput.trim(), passwordInput);
        await completeSignIn(user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  // On mount, complete sign-in via whichever signal fires first:
  //
  //   1. onAuthStateChanged — the RELIABLE one. After a Microsoft redirect,
  //      Firebase restores the session and fires this with the user, even when
  //      getRedirectResult flakily resolves null (the cause of the "have to try
  //      twice" bug in the Office WKWebView). currentUser is null on a genuine
  //      fresh open, so the first truthy callback is a real sign-in.
  //   2. getRedirectResult — still awaited so we can surface redirect errors and
  //      capture the Microsoft OAuth credential when it IS available.
  //
  // completeSignIn is guarded (completedRef) so only the first wins. If neither
  // yields a user, we show the sign-in form.
  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    reportDiag('mount', `web=${isOfficeWeb()} hostPlatform=${new URLSearchParams(window.location.search).get('hostPlatform')}`);

    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) completeSignIn(user);
    });

    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await completeSignIn(result.user, OAuthProvider.credentialFromResult(result));
          return; // success — keep the spinner, parent takes over
        }
      } catch (err) {
        console.error('[AuthDialog] getRedirectResult failed:', {
          code: err.code,
          message: err.message,
        });
        // Same reconciliation as the popup path: if the email is the SSO account,
        // exchange the pending MS credential for the canonical microsoft_{oid} uid.
        const reconciled = await reconcileMicrosoftIdentity(err);
        if (reconciled) { await completeSignIn(reconciled); return; }
        let userMessage = err.message || 'Microsoft sign-in failed. Please try again.';
        if (err.code === 'auth/unauthorized-domain') {
          userMessage = 'This domain isn\'t authorized for Microsoft sign-in. Contact support.';
        } else if (/AADSTS50011/i.test(err.message || '')) {
          userMessage = 'Microsoft sign-in is misconfigured (Azure redirect URI mismatch). Contact support.';
        }
        setError(userMessage);
      }
      // Only fall back to the form if a sign-in isn't already completing.
      if (!completedRef.current && !auth.currentUser) {
        setLoading(false);
        setShowEmailForm(true);
      }
    })();

    return () => { try { unsub(); } catch (e) { /* ignore */ } };
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: 2,
      px: 4,
      textAlign: 'center',
      bgcolor: 'background.default',
    }}>
      <Typography variant="h5" fontWeight={700} color="text.primary">
        {emailMode === 'reset' ? 'Reset password' : emailMode === 'signup' ? 'Create account' : 'Sign in to Restack'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {emailMode === 'reset' ? 'Enter your email to receive a reset link.' : `Connect your account to use Restack inside ${appName}.`}
      </Typography>

      {error && (
        <Alert
          severity={error.includes('Reset link sent') ? 'success' : 'error'}
          sx={{ width: '100%', maxWidth: 360, mb: 1 }}
        >
          {error}
        </Alert>
      )}

      {success ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" color="success.main" fontWeight={500}>
            Signed in! Returning to {appName}...
          </Typography>
          <CircularProgress size={20} />
        </Box>
      ) : showEmailForm ? (
        <Box sx={{ width: '100%', maxWidth: 340 }}>
          {/* Microsoft sign-in — primary CTA for a PowerPoint plugin */}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleMicrosoftSignIn}
            startIcon={
              <svg width="16" height="16" viewBox="0 0 23 23">
                <path fill="#f25022" d="M1 1h10v10H1z"/>
                <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                <path fill="#7fba00" d="M12 1h10v10H12z"/>
                <path fill="#ffb900" d="M12 12h10v10H12z"/>
              </svg>
            }
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: '10px', mb: 1 }}
          >
            Continue with Microsoft
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">or use email</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Box component="form" onSubmit={handleEmailSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {emailMode === 'signup' && (
              <TextField size="small" placeholder="Full name" value={nameInput} onChange={(e) => setNameInput(e.target.value)} fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
            )}
            <TextField size="small" type="email" placeholder="Email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} required fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }} />
            {emailMode !== 'reset' && (
              <TextField size="small" type={showPassword ? 'text' : 'password'} placeholder="Password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} required fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }} />
            )}
            <Button type="submit" variant="contained" disabled={emailLoading} fullWidth
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', py: 1, boxShadow: 'none' }}>
              {emailLoading ? <CircularProgress size={18} color="inherit" /> :
                emailMode === 'reset' ? 'Send reset link' :
                emailMode === 'signup' ? 'Create account' : 'Sign in'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5 }}>
            {emailMode === 'signin' && (
              <>
                <Link component="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { setEmailMode('signup'); setError(null); }}>Create account</Link>
                <Link component="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { setEmailMode('reset'); setError(null); }}>Forgot password?</Link>
              </>
            )}
            {(emailMode === 'signup' || emailMode === 'reset') && (
              <Link component="button" variant="caption" underline="hover" color="text.secondary" onClick={() => { setEmailMode('signin'); setError(null); }}>Back to sign in</Link>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary">
            Signing in...
          </Typography>
        </Box>
      )}
    </Box>
  );
}
